import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Building, Save, Upload, Image as ImageIcon } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CompanySettings = () => {
  const [settings, setSettings] = useState({
    company_name: '',
    logo_url: '',
    address_street: '',
    address_city: '',
    address_province: '',
    address_postal: '',
    phone: '',
    email: '',
    website: '',
    gst_number: '',
    business_number: '',
    tagline: '',
    message_statement: '',
    message_pdf: '',
    message_estimate: '',
    message_invoice: '',
    disclaimer_estimate: '',
    disclaimer_invoice: '',
    smtp_host: 'smtp-mail.outlook.com',
    smtp_port: 587,
    smtp_email: '',
    smtp_password: '',
    email_subject_template: '{document_type} #{document_number} from {company_name}',
    email_body_template: 'Dear {client_name},\n\nPlease find attached {document_type} #{document_number} for your review.\n\nTotal Amount: ${total}\n\nIf you have any questions, please don\'t hesitate to contact us.\n\nBest regards,\n{company_name} Team',
    fiscal_year: new Date().getFullYear(),
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/company-settings`);
      // Merge response data with defaults, replacing null/undefined with empty strings
      const cleanedData = {};
      Object.keys(settings).forEach(key => {
        cleanedData[key] = response.data[key] ?? settings[key] ?? '';
      });
      setSettings(prev => ({...prev, ...cleanedData}));
      
      // Load logo preview if exists
      if (response.data.logo_url) {
        try {
          const logoResponse = await axios.get(`${API}/company-settings/logo`, {
            responseType: 'blob',
          });
          const logoUrl = URL.createObjectURL(logoResponse.data);
          setLogoPreview(logoUrl);
        } catch (error) {
          console.log('No logo found');
        }
      }
    } catch (error) {
      toast.error('Failed to load company settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!settings.company_name) {
      toast.error('Company name is required');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/company-settings`, settings);
      toast.success('Company settings saved successfully!');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setUploadingLogo(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/company-settings/logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setLogoPreview(previewUrl);
      
      setSettings({ ...settings, logo_url: response.data.logo_url });
      toast.success('Logo uploaded successfully!');
    } catch (error) {
      toast.error('Failed to upload logo');
    } finally {
      setUploadingLogo(false);
      e.target.value = '';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-primary text-xl font-mono uppercase tracking-widest">Loading...</div>
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="company-settings-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl md:text-5xl font-black tracking-tight uppercase text-foreground mb-2" data-testid="company-settings-title">
          COMPANY SETTINGS
        </h1>
        <p className="text-sm font-mono uppercase tracking-wide text-muted-foreground">
          Manage company information for quotes, invoices, and PDFs
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Company Branding */}
        <div className="lg:col-span-1">
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="text-xl font-semibold tracking-wide uppercase text-muted-foreground">
                <Building className="inline mr-2" size={20} />
                Company Branding
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label htmlFor="company-name" className="uppercase text-xs tracking-wide">Company Name *</Label>
                <Input
                  id="company-name"
                  value={settings.company_name}
                  onChange={(e) => setSettings({ ...settings, company_name: e.target.value })}
                  className="mt-1 font-bold text-lg"
                  placeholder="PSM WELDING"
                  data-testid="company-name-input"
                />
              </div>

              <div>
                <Label htmlFor="tagline" className="uppercase text-xs tracking-wide">Tagline</Label>
                <Input
                  id="tagline"
                  value={settings.tagline}
                  onChange={(e) => setSettings({ ...settings, tagline: e.target.value })}
                  className="mt-1"
                  placeholder="Industrial Management System"
                  data-testid="tagline-input"
                />
              </div>

              {/* Logo Upload */}
              <div>
                <Label className="uppercase text-xs tracking-wide mb-2 block">Company Logo</Label>
                <div className="border-2 border-dashed border-border rounded-sm p-6 text-center">
                  {logoPreview ? (
                    <div className="space-y-3">
                      <img
                        src={logoPreview}
                        alt="Company Logo"
                        className="mx-auto max-h-32 object-contain"
                      />
                      <p className="text-xs text-muted-foreground">Logo uploaded</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <ImageIcon className="mx-auto text-muted-foreground" size={48} />
                      <p className="text-sm text-muted-foreground">No logo uploaded</p>
                    </div>
                  )}
                  
                  <input
                    type="file"
                    id="logo-upload"
                    className="hidden"
                    onChange={handleLogoUpload}
                    accept="image/*"
                    disabled={uploadingLogo}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    className="mt-4 font-mono uppercase"
                    disabled={uploadingLogo}
                    onClick={() => document.getElementById('logo-upload').click()}
                  >
                    <Upload size={14} className="mr-2" />
                    {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                  </Button>
                  <p className="text-xs text-muted-foreground mt-2">
                    PNG, JPG up to 5MB
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Contact & Business Info */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="text-xl font-semibold tracking-wide uppercase text-muted-foreground">
                Contact Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="phone" className="uppercase text-xs tracking-wide">Phone Number</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={settings.phone}
                    onChange={(e) => setSettings({ ...settings, phone: e.target.value })}
                    className="mt-1 font-mono"
                    placeholder="(555) 123-4567"
                    data-testid="phone-input"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="uppercase text-xs tracking-wide">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    value={settings.email}
                    onChange={(e) => setSettings({ ...settings, email: e.target.value })}
                    className="mt-1 font-mono"
                    placeholder="info@psmwelding.com"
                    data-testid="email-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="website" className="uppercase text-xs tracking-wide">Website (Optional)</Label>
                <Input
                  id="website"
                  type="url"
                  value={settings.website}
                  onChange={(e) => setSettings({ ...settings, website: e.target.value })}
                  className="mt-1 font-mono"
                  placeholder="www.psmwelding.com"
                  data-testid="website-input"
                />
              </div>
            </CardContent>
          </Card>

          <Card className="border-border">
            <CardHeader>
              <CardTitle className="text-xl font-semibold tracking-wide uppercase text-muted-foreground">
                Business Address
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="address-street" className="uppercase text-xs tracking-wide">Street Address</Label>
                <Input
                  id="address-street"
                  value={settings.address_street}
                  onChange={(e) => setSettings({ ...settings, address_street: e.target.value })}
                  className="mt-1"
                  placeholder="123 Industrial Avenue"
                  data-testid="address-street-input"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="address-city" className="uppercase text-xs tracking-wide">City</Label>
                  <Input
                    id="address-city"
                    value={settings.address_city}
                    onChange={(e) => setSettings({ ...settings, address_city: e.target.value })}
                    className="mt-1"
                    placeholder="Toronto"
                    data-testid="address-city-input"
                  />
                </div>
                <div>
                  <Label htmlFor="address-province" className="uppercase text-xs tracking-wide">Province</Label>
                  <Input
                    id="address-province"
                    value={settings.address_province}
                    onChange={(e) => setSettings({ ...settings, address_province: e.target.value })}
                    className="mt-1"
                    placeholder="ON"
                    data-testid="address-province-input"
                  />
                </div>
                <div>
                  <Label htmlFor="address-postal" className="uppercase text-xs tracking-wide">Postal Code</Label>
                  <Input
                    id="address-postal"
                    value={settings.address_postal}
                    onChange={(e) => setSettings({ ...settings, address_postal: e.target.value })}
                    className="mt-1 font-mono"
                    placeholder="M1A 2B3"
                    data-testid="address-postal-input"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-border">
            <CardHeader>
              <CardTitle className="text-xl font-semibold tracking-wide uppercase text-muted-foreground">
                Business Numbers
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="gst-number" className="uppercase text-xs tracking-wide">GST Registration Number</Label>
                  <Input
                    id="gst-number"
                    value={settings.gst_number}
                    onChange={(e) => setSettings({ ...settings, gst_number: e.target.value })}
                    className="mt-1 font-mono"
                    placeholder="123456789RT0001"
                    data-testid="gst-number-input"
                  />
                </div>
                <div>
                  <Label htmlFor="business-number" className="uppercase text-xs tracking-wide">Business Number</Label>
                  <Input
                    id="business-number"
                    value={settings.business_number}
                    onChange={(e) => setSettings({ ...settings, business_number: e.target.value })}
                    className="mt-1 font-mono"
                    placeholder="123456789"
                    data-testid="business-number-input"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="fiscal-year" className="uppercase text-xs tracking-wide">Fiscal Year</Label>
                <Input
                  id="fiscal-year"
                  type="number"
                  value={settings.fiscal_year}
                  onChange={(e) => setSettings({ ...settings, fiscal_year: parseInt(e.target.value) || new Date().getFullYear() })}
                  className="mt-1 font-mono"
                  placeholder={new Date().getFullYear().toString()}
                  data-testid="fiscal-year-input"
                  min="2000"
                  max="2100"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Year used in document numbers (Q-YYYY-XXX, INV-YYYY-XXX). Numbers reset to 001 on January 1st.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="border-border">
            <CardHeader>
              <CardTitle className="text-xl font-semibold tracking-wide uppercase text-muted-foreground">
                PDF Messages & Content
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="message-statement" className="uppercase text-xs tracking-wide">Message on Statement</Label>
                  <textarea
                    id="message-statement"
                    value={settings.message_statement}
                    onChange={(e) => setSettings({ ...settings, message_statement: e.target.value })}
                    className="mt-1 w-full px-3 py-2 bg-background border border-input rounded-sm text-sm resize-vertical"
                    rows={3}
                    placeholder="Thank you for choosing our services..."
                    data-testid="message-statement-input"
                  />
                </div>
                <div>
                  <Label htmlFor="message-pdf" className="uppercase text-xs tracking-wide">General PDF Message</Label>
                  <textarea
                    id="message-pdf"
                    value={settings.message_pdf}
                    onChange={(e) => setSettings({ ...settings, message_pdf: e.target.value })}
                    className="mt-1 w-full px-3 py-2 bg-background border border-input rounded-sm text-sm resize-vertical"
                    rows={3}
                    placeholder="Professional fabrication services..."
                    data-testid="message-pdf-input"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="message-estimate" className="uppercase text-xs tracking-wide">Message on Estimates/Quotes</Label>
                  <textarea
                    id="message-estimate"
                    value={settings.message_estimate}
                    onChange={(e) => setSettings({ ...settings, message_estimate: e.target.value })}
                    className="mt-1 w-full px-3 py-2 bg-background border border-input rounded-sm text-sm resize-vertical"
                    rows={3}
                    placeholder="We look forward to working with you..."
                    data-testid="message-estimate-input"
                  />
                </div>
                <div>
                  <Label htmlFor="message-invoice" className="uppercase text-xs tracking-wide">Message on Invoices</Label>
                  <textarea
                    id="message-invoice"
                    value={settings.message_invoice}
                    onChange={(e) => setSettings({ ...settings, message_invoice: e.target.value })}
                    className="mt-1 w-full px-3 py-2 bg-background border border-input rounded-sm text-sm resize-vertical"
                    rows={3}
                    placeholder="Thank you for your business..."
                    data-testid="message-invoice-input"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-border">
            <CardHeader>
              <CardTitle className="text-xl font-semibold tracking-wide uppercase text-muted-foreground">
                Terms & Disclaimers
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="disclaimer-estimate" className="uppercase text-xs tracking-wide">Estimate/Quote Disclaimer</Label>
                <textarea
                  id="disclaimer-estimate"
                  value={settings.disclaimer_estimate}
                  onChange={(e) => setSettings({ ...settings, disclaimer_estimate: e.target.value })}
                  className="mt-1 w-full px-3 py-2 bg-background border border-input rounded-sm text-sm resize-vertical"
                  rows={3}
                  placeholder="This estimate is valid for 30 days. To accept this quote, please reply to this email or call us directly. Payment terms: Net 15 days."
                  data-testid="disclaimer-estimate-input"
                />
                <p className="text-xs text-muted-foreground mt-1">Appears at bottom of quotes/estimates</p>
              </div>
              
              <div>
                <Label htmlFor="disclaimer-invoice" className="uppercase text-xs tracking-wide">Invoice Disclaimer</Label>
                <textarea
                  id="disclaimer-invoice"
                  value={settings.disclaimer_invoice}
                  onChange={(e) => setSettings({ ...settings, disclaimer_invoice: e.target.value })}
                  className="mt-1 w-full px-3 py-2 bg-background border border-input rounded-sm text-sm resize-vertical"
                  rows={3}
                  placeholder="Payment is due within the terms specified. Late payments may incur additional charges."
                  data-testid="disclaimer-invoice-input"
                />
                <p className="text-xs text-muted-foreground mt-1">Appears at bottom of invoices</p>
              </div>
            </CardContent>
          </Card>

          {/* Email Configuration */}
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="text-xl font-semibold tracking-wide uppercase text-muted-foreground">
                Email Configuration
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="smtp-host" className="uppercase text-xs tracking-wide">SMTP Server</Label>
                  <Input
                    id="smtp-host"
                    value={settings.smtp_host}
                    onChange={(e) => setSettings({ ...settings, smtp_host: e.target.value })}
                    className="mt-1 font-mono"
                    placeholder="smtp-mail.outlook.com"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Outlook SMTP server</p>
                </div>
                <div>
                  <Label htmlFor="smtp-port" className="uppercase text-xs tracking-wide">SMTP Port</Label>
                  <Input
                    id="smtp-port"
                    type="number"
                    value={settings.smtp_port}
                    onChange={(e) => setSettings({ ...settings, smtp_port: parseInt(e.target.value) || 587 })}
                    className="mt-1 font-mono"
                    placeholder="587"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Usually 587 for TLS</p>
                </div>
              </div>
              
              <div>
                <Label htmlFor="smtp-email" className="uppercase text-xs tracking-wide">SMTP Email Address *</Label>
                <Input
                  id="smtp-email"
                  type="email"
                  value={settings.smtp_email}
                  onChange={(e) => setSettings({ ...settings, smtp_email: e.target.value })}
                  className="mt-1 font-mono"
                  placeholder="your-email@outlook.com"
                />
                <p className="text-xs text-muted-foreground mt-1">Your Outlook email address</p>
              </div>
              
              <div>
                <Label htmlFor="smtp-password" className="uppercase text-xs tracking-wide">SMTP Password *</Label>
                <Input
                  id="smtp-password"
                  type="password"
                  value={settings.smtp_password}
                  onChange={(e) => setSettings({ ...settings, smtp_password: e.target.value })}
                  className="mt-1 font-mono"
                  placeholder="••••••••"
                />
                <p className="text-xs text-muted-foreground mt-1">Your Outlook password or app password</p>
              </div>
              
              <div>
                <Label htmlFor="email-subject" className="uppercase text-xs tracking-wide">Email Subject Template</Label>
                <Input
                  id="email-subject"
                  value={settings.email_subject_template}
                  onChange={(e) => setSettings({ ...settings, email_subject_template: e.target.value })}
                  className="mt-1 font-mono text-sm"
                  placeholder="{document_type} #{document_number} from {company_name}"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Variables: {'{document_type}'}, {'{document_number}'}, {'{company_name}'}, {'{client_name}'}, {'{total}'}
                </p>
              </div>
              
              <div>
                <Label htmlFor="email-body" className="uppercase text-xs tracking-wide">Email Body Template</Label>
                <textarea
                  id="email-body"
                  value={settings.email_body_template}
                  onChange={(e) => setSettings({ ...settings, email_body_template: e.target.value })}
                  className="mt-1 w-full px-3 py-2 bg-background border border-input rounded-sm text-sm resize-vertical font-mono"
                  rows={6}
                  placeholder="Dear {client_name},..."
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Same variables available. System will automatically insert document type (Quote/Invoice).
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Save Button */}
          <Button
            onClick={handleSave}
            disabled={saving}
            className="w-full font-mono uppercase tracking-wider text-lg py-6"
            data-testid="save-settings-button"
          >
            <Save size={20} className="mr-2" />
            {saving ? 'Saving...' : 'Save Company Settings'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CompanySettings;
